import React, { useState, useEffect, useCallback } from "react";
import { obtenerEstructuraCompleta } from "../services/api";
import { enviarComando } from "../services/comandoService.js";
import WebSocketStatus from "./socket.jsx";

function Configuracion() {
  const [equipos, setEquipos] = useState([]);
  const [equipoSeleccionado, setEquipoSeleccionado] = useState(null);
  const [ip, setIp] = useState("");
  const [equipoActivo, setEquipoActivo] = useState(false);
  const [historial, setHistorial] = useState([]);

  useEffect(() => {
    const cargarEquipos = async () => {
      try {
        const data = await obtenerEstructuraCompleta();
        if (data && data.estado && data.datos.length > 0) {
          const modulos = data.datos[0].campos[0].modulos ?? [];
          setEquipos(modulos);
        }
      } catch (error) {
        console.error("Error al cargar equipos:", error);
      }
    };
    cargarEquipos();
  }, []);

  const getHoraActual = () => {
    const ahora = new Date();
    return ahora.toLocaleTimeString("es-CL", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const agregarHistorial = useCallback((msg) => {
    const hora = getHoraActual();
    setHistorial((prev) => [...prev, `[${hora}] ${msg}`]);
  }, []);

  const seleccionarEquipo = (equipo) => {
    setEquipoSeleccionado(equipo);
    setIp(equipo.ip ?? "192.168.1.23");
    setEquipoActivo(false); // Por defecto desactivado al cambiar
  };

  const guardarIp = () => {
    console.log("✅ Nueva IP guardada para", equipoSeleccionado.moduloNombre, ":", ip);
  };

  const enviarComandoPrueba = async () => {
    if (!equipoSeleccionado) {
      alert("Selecciona un equipo primero.");
      return;
    }
    if (!ip) {
      alert("Configura la IP primero.");
      return;
    }

    try {
      const mensajePrueba = "comando-de-prueba";
      const respuesta = await enviarComando(ip, mensajePrueba);
      agregarHistorial(`✅ ${equipoSeleccionado.moduloNombre}: ${respuesta}`);
    } catch (error) {
      agregarHistorial(`❌ ${equipoSeleccionado.moduloNombre}: ${error.message}`);
    }
  };

  const toggleEquipoActivo = () => {
    setEquipoActivo((prev) => !prev);
    agregarHistorial(`🔄 Equipo ${!equipoActivo ? "activado" : "desactivado"}`);
  };

  return (
    <div className="flex h-screen">
      {/* Lado izquierdo: listado de equipos */}
      <div className="w-1/4 border-r overflow-y-auto">
        <h2 className="text-xl font-bold p-4">Equipos</h2>
        {equipos.map((equipo) => (
          <div
            key={equipo.moduloId}
            className={`p-4 border-b cursor-pointer hover:bg-gray-100 ${
              equipoSeleccionado?.moduloId === equipo.moduloId
                ? "bg-yellow-100 font-semibold"
                : ""
            }`}
            onClick={() => seleccionarEquipo(equipo)}
          >
            {equipo.moduloNombre}
          </div>
        ))}
      </div>

      {/* Lado central: configuración del equipo seleccionado */}
      <div className="flex-1 p-6 overflow-y-auto">
        {equipoSeleccionado ? (
          <>
            <h2 className="text-2xl font-bold mb-4">
              Configuración: {equipoSeleccionado.moduloNombre}
            </h2>

            {/* ✅ Detalles del equipo */}
            <div className="mb-6 bg-gray-50 p-4 rounded border">
              <h3 className="text-lg font-semibold mb-2">Detalles del equipo</h3>
              <p><span className="font-medium">ID:</span> {equipoSeleccionado.moduloId}</p>
              <p><span className="font-medium">Hash:</span> {equipoSeleccionado.codigoUnicoHash}</p>
              <p><span className="font-medium">Categoría:</span> {equipoSeleccionado.categoriaNombre}</p>
              <p><span className="font-medium">Estado:</span> {equipoSeleccionado.estadoDescripcion}</p>
              <p><span className="font-medium">Creación:</span> {equipoSeleccionado.fechaCreacion}</p>
              <p><span className="font-medium">Actualización:</span> {equipoSeleccionado.fechaActualizacion}</p>
              <p><span className="font-medium">Sectores:</span> {equipoSeleccionado.sectores?.length ?? 0}</p>
            </div>

            {/* Activar/Desactivar equipo */}
            <div className="mb-6">
              <button
                onClick={toggleEquipoActivo}
                className={`px-4 py-2 rounded text-white ${
                  equipoActivo
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-green-600 hover:bg-green-700"
                }`}
              >
                {equipoActivo ? "Desactivar equipo" : "Activar equipo"}
              </button>
            </div>

            {/* Configuración de IP */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                IP del equipo:
              </label>
              <input
                type="text"
                value={ip}
                onChange={(e) => setIp(e.target.value)}
                className="border rounded px-3 py-2 w-full max-w-xs"
                placeholder="192.168.x.x"
              />
              <button
                onClick={guardarIp}
                className="mt-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                Guardar IP
              </button>

              {/* Botón para enviar comando de prueba */}
              <button
                onClick={enviarComandoPrueba}
                className="mt-2 ml-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Enviar comando de prueba
              </button>
            </div>

            {/* WebSocketStatus: renderiza solo si está activo */}
            {equipoActivo && (
              <WebSocketStatus
                equipoHash={equipoSeleccionado.codigoUnicoHash}
                ip={ip}
                agregarHistorial={agregarHistorial}
              />
            )}
          </>
        ) : (
          <p className="text-gray-500">Selecciona un equipo para configurarlo.</p>
        )}
      </div>

      {/* Lado derecho: historial de comandos */}
      <div className="w-1/4 border-l overflow-y-auto p-4">
        <h2 className="text-xl font-bold mb-4">📜 Historial de Comandos</h2>
        {historial.length === 0 ? (
          <p className="text-gray-500">No hay mensajes aún.</p>
        ) : (
          <div className="space-y-2 text-sm">
            {historial.map((msg, idx) => (
              <div key={idx} className="bg-gray-100 p-2 rounded">
                {msg}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Configuracion;
